// src/components/employee-steps/StepPersonalInfo.js

import React, { useState, useEffect, useRef } from "react";
import Icon from "../icons/Icon";

export const StepPersonalInfo = ({
  formData,
  handleInputChange,
  handleNext,
  handleCancel,
  errors,
  setErrors,
  employeeIdType,
  setEmployeeIdType,
  nextEmployeeId,
  checkEmployeeIdAvailable,
  isEdit = false,
  nextStepLabel = "Next →",
}) => {
  const firstInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  // Local state for real-time validation
  const [localErrors, setLocalErrors] = useState({});
  // True while the async manual-entry uniqueness check is in flight.
  const [checkingId, setCheckingId] = useState(false);

  // Employee ID validation (4 digits only)
  const handleEmployeeIdChange = (value) => {
    // Only allow numeric input
    const numericValue = value.replace(/\D/g, "");

    // Limit to 4 digits
    if (numericValue.length <= 4) {
      handleInputChange("root", "employeeId", numericValue);

      // Real-time validation
      if (employeeIdType === "manual") {
        if (numericValue.length < 4) {
          setLocalErrors((prev) => ({
            ...prev,
            employeeId: "Employee ID must be 4 digits",
          }));
        } else {
          setLocalErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.employeeId;
            return newErrors;
          });
        }
      }
    }
  };

  // Mobile number validation and formatting
  const handleMobileChange = (value) => {
    // Remove all non-numeric characters
    let numericValue = value.replace(/\D/g, "");

    // Handle different formats
    if (numericValue.startsWith("63")) {
      // International format (+63)
      numericValue = numericValue.substring(0, 12); // +63 + 10 digits
    } else if (numericValue.startsWith("0")) {
      // Local format (09XX)
      numericValue = numericValue.substring(0, 11); // 0 + 10 digits
    }

    handleInputChange("personalInfo", "mobile", numericValue);

    // Real-time validation
    validateMobileNumber(numericValue);
  };

  // Format mobile number for display
  const formatMobileDisplay = (value) => {
    if (!value) return "";

    // Remove non-numeric characters
    const numericValue = value.replace(/\D/g, "");

    if (numericValue.startsWith("63") && numericValue.length > 2) {
      // Format: +63 9XX XXX XXXX
      const formatted = `+${numericValue.slice(0, 2)} ${numericValue.slice(
        2,
        5
      )} ${numericValue.slice(5, 8)} ${numericValue.slice(8)}`;
      return formatted.trim();
    } else if (numericValue.startsWith("0") && numericValue.length > 0) {
      // Format: 09XX XXX XXXX
      const formatted = `${numericValue.slice(0, 4)} ${numericValue.slice(
        4,
        7
      )} ${numericValue.slice(7)}`;
      return formatted.trim();
    }

    return numericValue;
  };

  // Validate mobile number
  const validateMobileNumber = (value) => {
    // Mobile is now optional, return true if empty
    if (!value) {
      setLocalErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.mobile;
        return newErrors;
      });
      return true;
    }

    const numericValue = value.replace(/\D/g, "");

    // Check if it's a valid Philippine mobile number
    const isValidInternational =
      numericValue.startsWith("639") && numericValue.length === 12;
    const isValidLocal =
      numericValue.startsWith("09") && numericValue.length === 11;

    if (!isValidInternational && !isValidLocal) {
      setLocalErrors((prev) => ({
        ...prev,
        mobile: "Invalid Philippine mobile number format",
      }));
      return false;
    }

    setLocalErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.mobile;
      return newErrors;
    });
    return true;
  };

  // Real-time email validation
  const handleEmailChange = (value) => {
    handleInputChange("personalInfo", "email", value);

    // Checked trimmed: the stored value keeps a trailing space until blur (see the
    // input), and the space that ends a macOS text replacement must not flag it.
    if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      setLocalErrors((prev) => ({
        ...prev,
        email: "Invalid email format",
      }));
    } else {
      setLocalErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
    }
  };

  // Format date display for MM/DD/YYYY
  const handleDateChange = (value) => {
    handleInputChange("personalInfo", "dateOfBirth", value);

    // Date of birth is now optional
    setLocalErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.dateOfBirth;
      return newErrors;
    });
  };

  // Format date for display (e.g., Jan 22, 1990)
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  // Check if all mandatory fields are valid
  const isFormValid = () => {
    // Check Employee ID
    if (
      employeeIdType === "manual" &&
      (!formData.employeeId || formData.employeeId.length !== 4)
    ) {
      return false;
    }

    // Check personal info fields (only firstName and lastName are mandatory)
    const { firstName, lastName, email, mobile } =
      formData.personalInfo;

    if (
      !firstName?.trim() ||
      !lastName?.trim()
    ) {
      return false;
    }

    // Check email format if provided
    if (email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return false;
    }

    // Check mobile format if provided
    if (mobile?.trim()) {
      const numericMobile = mobile.replace(/\D/g, "");
      const isValidMobile =
        (numericMobile.startsWith("639") && numericMobile.length === 12) ||
        (numericMobile.startsWith("09") && numericMobile.length === 11);
      if (!isValidMobile) {
        return false;
      }
    }

    return Object.keys(localErrors).length === 0;
  };

  // Handle next button click
  const handleNextClick = async () => {
    if (checkingId) return; // re-entrancy guard while a check is in flight

    // Validate all fields before proceeding
    const validationErrors = {};

    if (
      employeeIdType === "manual" &&
      (!formData.employeeId || formData.employeeId.length !== 4)
    ) {
      validationErrors.employeeId = "Employee ID must be 4 digits";
    }

    if (!formData.personalInfo.firstName?.trim()) {
      validationErrors.firstName = "First name is required";
    }

    if (!formData.personalInfo.lastName?.trim()) {
      validationErrors.lastName = "Last name is required";
    }

    // Email is optional but validate format if provided
    if (
      formData.personalInfo.email?.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalInfo.email)
    ) {
      validationErrors.email = "Invalid email format";
    }

    // Mobile is optional but validate format if provided
    if (!validateMobileNumber(formData.personalInfo.mobile)) {
      validationErrors.mobile = localErrors.mobile;
    }

    // Date of birth is now optional

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLocalErrors(validationErrors);
      return;
    }

    // Manual entry: reject an Employee ID that already exists in this company.
    // The service-layer guard in employeeService.createEmployee is authoritative;
    // this is early feedback so the admin sees it on Step 1.
    if (employeeIdType === "manual" && checkEmployeeIdAvailable) {
      setCheckingId(true);
      let available = true;
      try {
        available = await checkEmployeeIdAvailable(formData.employeeId);
      } catch (err) {
        available = true; // best-effort; let the authoritative guard decide
      }
      setCheckingId(false);
      if (!available) {
        const dupError = { employeeId: "This Employee ID is already in use" };
        setErrors(dupError);
        setLocalErrors(dupError);
        return;
      }
    }

    handleNext();
  };

  // Merge errors from parent and local errors
  const displayErrors = { ...errors, ...localErrors };

  return (
    <>
      <div className="add-employee-form-container">
        {/* Employee ID Configuration Section */}
        <div className={`form-section ${isEdit ? "form-section--disabled" : ""}`}>
          <div className="form-section-header">
            <div className="form-section-icon"><Icon name="credit-card" /></div>
            <h2 className="form-section-title">
              Employee ID Configuration
              {isEdit && <span className="read-only-badge"><Icon name="lock" size={12} aria-hidden="true" /> Read-Only</span>}
            </h2>
          </div>

          <div className="form-group">
            <label className="form-label">Employee ID Generation Method</label>
            <div className="id-type-toggle">
              <label className="toggle-option">
                <input
                  type="radio"
                  name="employeeIdType"
                  value="auto"
                  checked={employeeIdType === "auto"}
                  disabled={isEdit}
                  onChange={(e) => {
                    setEmployeeIdType(e.target.value);
                    // Clear employee ID and errors when switching to auto
                    handleInputChange("root", "employeeId", "");
                    setLocalErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.employeeId;
                      return newErrors;
                    });
                  }}
                />
                <div className="toggle-label">
                  <span className="toggle-label-title">Auto-Generate</span>
                  <span className="toggle-label-desc">
                    System generates 4-digit ID
                  </span>
                </div>
              </label>

              <label className="toggle-option">
                <input
                  type="radio"
                  name="employeeIdType"
                  value="manual"
                  checked={employeeIdType === "manual"}
                  disabled={isEdit}
                  onChange={(e) => setEmployeeIdType(e.target.value)}
                />
                <div className="toggle-label">
                  <span className="toggle-label-title">Manual Entry</span>
                  <span className="toggle-label-desc">
                    For existing employee IDs
                  </span>
                </div>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Employee ID <span className="required">*</span>
            </label>
            {isEdit ? (
              <div className="id-preview">
                <div className="id-preview-label">Current ID: {formData.employeeId || "N/A"}</div>
                <div className="id-preview-note">
                  Employee ID cannot be changed after creation
                </div>
              </div>
            ) : employeeIdType === "auto" ? (
              <div className="id-preview">
                <div className="id-preview-label">Next ID:{nextEmployeeId}</div>
                <div className="id-preview-note">
                  This ID will be automatically assigned when you create the
                  employee
                </div>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={formData.employeeId || ""}
                  onChange={(e) => handleEmployeeIdChange(e.target.value)}
                  className={`form-input ${
                    displayErrors.employeeId ? "form-input--error" : ""
                  }`}
                  placeholder="0000"
                  maxLength="4"
                />
                {displayErrors.employeeId && (
                  <span className="form-error-text">
                    {displayErrors.employeeId}
                  </span>
                )}
                {!displayErrors.employeeId && (
                  <small className="form-help-text">
                    Enter 4-digit numeric employee ID
                  </small>
                )}
              </>
            )}
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="form-section">
          <div className="form-section-header">
            <div className="form-section-icon"><Icon name="user" /></div>
            <h2 className="form-section-title">Personal Information</h2>
          </div>

          <div className="form-group">
            <label className="form-label">
              Last Name <span className="required">*</span>
            </label>
            <input
              ref={firstInputRef}
              type="text"
              value={formData.personalInfo.lastName}
              onChange={(e) => {
                handleInputChange("personalInfo", "lastName", e.target.value);
                if (e.target.value.trim()) {
                  setLocalErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.lastName;
                    return newErrors;
                  });
                }
              }}
              className={`form-input ${
                displayErrors.lastName ? "form-input--error" : ""
              }`}
              placeholder="Dela Cruz"
            />
            {displayErrors.lastName && (
              <span className="form-error-text">{displayErrors.lastName}</span>
            )}
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.personalInfo.firstName}
                onChange={(e) => {
                  handleInputChange(
                    "personalInfo",
                    "firstName",
                    e.target.value
                  );
                  if (e.target.value.trim()) {
                    setLocalErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.firstName;
                      return newErrors;
                    });
                  }
                }}
                className={`form-input ${
                  displayErrors.firstName ? "form-input--error" : ""
                }`}
                placeholder="Juan"
              />
              {displayErrors.firstName && (
                <span className="form-error-text">{displayErrors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Middle Name
              </label>
              <input
                type="text"
                value={formData.personalInfo.middleName || ""}
                onChange={(e) => {
                  handleInputChange("personalInfo", "middleName", e.target.value);
                }}
                className="form-input"
                placeholder="Santos"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Email Address
            </label>
            <input
              type="text"
              inputMode="email"
              autoCapitalize="none"
              value={formData.personalInfo.email}
              // Trim on blur, not on every keystroke: stripping the space that
              // ends a macOS text replacement leaves its suggestion open (BL-105).
              onChange={(e) => handleEmailChange(e.target.value)}
              // Only when trimming changes something: every handleInputChange
              // marks the wizard dirty, and tabbing past must not count as an edit.
              onBlur={(e) => {
                const trimmed = e.target.value.trim();
                if (trimmed !== e.target.value) handleEmailChange(trimmed);
              }}
              className={`form-input ${
                displayErrors.email ? "form-input--error" : ""
              }`}
              placeholder="juan.delacruz@example.com"
            />
            {displayErrors.email && (
              <span className="form-error-text">{displayErrors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Mobile Number
            </label>
            <input
              type="tel"
              value={formatMobileDisplay(formData.personalInfo.mobile)}
              onChange={(e) => handleMobileChange(e.target.value)}
              className={`form-input ${
                displayErrors.mobile ? "form-input--error" : ""
              }`}
              placeholder="+63 9XX XXX XXXX"
            />
            {displayErrors.mobile && (
              <span className="form-error-text">{displayErrors.mobile}</span>
            )}
            {!displayErrors.mobile && (
              <small className="form-help-text">
                Format: 09XX XXX XXXX or +639XXXXXXXXX
              </small>
            )}
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                Date of Birth
              </label>
              <input
                type="date"
                value={formData.personalInfo.dateOfBirth}
                onChange={(e) => handleDateChange(e.target.value)}
                className={`form-input ${
                  displayErrors.dateOfBirth ? "form-input--error" : ""
                }`}
              />
              {displayErrors.dateOfBirth && (
                <span className="form-error-text">
                  {displayErrors.dateOfBirth}
                </span>
              )}
              {formData.personalInfo.dateOfBirth &&
                !displayErrors.dateOfBirth && (
                  <span className="date-format-helper">
                    {formatDateDisplay(formData.personalInfo.dateOfBirth)}
                  </span>
                )}
            </div>

            <div className="form-group">
              <label className="form-label">Gender</label>
              <select
                value={formData.personalInfo.gender}
                onChange={(e) =>
                  handleInputChange("personalInfo", "gender", e.target.value)
                }
                className="form-select"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Marital Status</label>
            <select
              value={formData.personalInfo.maritalStatus}
              onChange={(e) =>
                handleInputChange(
                  "personalInfo",
                  "maritalStatus",
                  e.target.value
                )
              }
              className="form-select"
            >
              <option value="">Select Status</option>
              <option value="single">Single</option>
              <option value="married">Married</option>
              <option value="widowed">Widowed</option>
              <option value="separated">Separated</option>
            </select>
          </div>
        </div>
      </div>

      {/* Form Actions - Outside the grid container */}
      <div className="add-employee-form-actions">
        <button onClick={handleCancel} className="btn btn--secondary">
          Cancel
        </button>
        <button
          onClick={handleNextClick}
          className="btn btn--primary"
          disabled={!isFormValid() || checkingId}
        >
          {checkingId ? "Checking…" : nextStepLabel}
        </button>
      </div>
    </>
  );
};
